import { useRef, useState, memo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import { useReactFlow } from "reactflow";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SoftTypography from "components/SoftTypography";
import TriggerFormWrapper from "../TriggerFormWrapper";
import FormField from "layouts/Flow Designer/components/FormField";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import Comments from "layouts/Flow Designer/components/FlowPropertiesPanel/components/Comments";
import validations from "./schemas/validations";
import initialValues from "./schemas/initialValues";
import { form } from "./schemas/form";
import { getEnv } from "helpers/environment";
import { useAuth0 } from "@auth0/auth0-react";
import { ThemedAddCircleOutlineIcon } from "assets/CXFIcons/Actions";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import { closeTriggerPropertiesPanel, updateFlowTrigger, clearSelectedTrigger } from "redux/actions/flowDesigner/triggersActionCreator";
import { TabContextApi } from "../../../../context";
import { updateContextTrigger } from "redux/actions/flowDesigner/editorContextActionCreator";
import { showStartNodePanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { Actions } from "flexlayout-react";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";
import { setSelectedTrigger } from "redux/actions/flowDesigner/triggersActionCreator";
import FormTabs from "../FormTabs";

const TabPanel = ({ children, value, index }) => {
   return value === index ? <>{children}</> : null;
};

const McpTriggerForm = ({ selectedTrigger, allowEditWorkflow }) => {
   const formRef = useRef(null);
   const dispatch = useDispatch();
   const reactFlowInstance = useReactFlow();
   const { model } = useFlexLayout();
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { user } = useAuth0();
   const { autoSaveFlow } = useContext(TabContextApi);
   const selectedTriggerProps = selectedTrigger?.properties;
   const [submitting, setSubmitting] = useState(false);
   const [tabValue, setTabValue] = useState(0);
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   initialValues.endpointUrl = getEnv("EXECUTOR_API_SOCKET") + `/mcp?tenant_id=${user?.stripeId}&flow_id=${currentSavedFlow?.id}${isFlowActive === false ? "&draft=true" : ""}`;
   initialValues.triggerName = selectedTriggerProps?.triggerName || "";
   initialValues.description = selectedTriggerProps?.description || "";
   initialValues.parameters = selectedTriggerProps?.parameters || [];
   initialValues.notes = selectedTriggerProps?.notes || "";

   const {
      formId,
      formField: { endpointUrl, triggerName, description, parameters, notes }
   } = form;

   const targetUserId = user?.sub?.replace(/\|/g, "_");

   const handleChangeTab = (event, newValue) => {
      setTabValue(newValue);
   };

   const handleSubmit = (event) => {
      event.preventDefault();
      formRef.current.submitForm();
   };

   const normalizeParameters = (params = []) =>
      params.map((p) => ({
         ...p,
         paramType: typeof p.paramType === "object" && p.paramType !== null ? p.paramType.value : p.paramType
      }));

   const handleSaveSubmit = async (values, actions) => {
      if (isFlowActive || !allowEditWorkflow) return;

      const { endpointUrl, triggerName, description, parameters, notes } = values;

      setSubmitting(true);
      actions.setSubmitting(false);

      const triggerProps = {
         endpoint_url: endpointUrl,
         triggerName,
         description,
         parameters: normalizeParameters(parameters),
         notes
      };

      updateStoreAndSaveFlow(triggerProps);

      setSubmitting(false);
      // closeForm();
   };

   const updateStoreAndSaveFlow = (triggerProps) => {
      const currentTrigger = { ...selectedTrigger };
      currentTrigger.properties = { ...currentTrigger.properties, ...triggerProps };
      dispatch(updateContextTrigger(currentTrigger.label, currentSavedFlow?.name, triggerProps));
      dispatch(updateFlowTrigger(currentTrigger));
      dispatch(clearSelectedTrigger());
      autoSaveFlow(reactFlowInstance);
   };

   const closeForm = () => {
      const tabId = "startNodeProps";
      model.doAction(
         Actions.updateNodeAttributes(tabId, {
            component: FLEX_LAYOUT_COMPONENT.startNode,
            config: {}
         })
      );
      model.doAction(Actions.updateNodeAttributes(tabId, { name: "Start Node" }));
      model.doAction(Actions.selectTab(tabId));
      dispatch(setSelectedTrigger(null));
      dispatch(closeTriggerPropertiesPanel());
      dispatch(showStartNodePanel());
   };

   return (
      <>
         <TriggerFormWrapper onSubmit={handleSubmit} onCancel={closeForm} submitting={submitting} disableSubmit={isFlowActive || !allowEditWorkflow} tabValue={tabValue}>
            <Formik initialValues={initialValues} onSubmit={handleSaveSubmit} validationSchema={validations} innerRef={formRef} enableReinitialize>
               {({ values, errors, touched, setFieldValue }) => (
                  <Form id={formId} autoComplete="off">
                     <FormTabs tabValue={tabValue} onChange={handleChangeTab} />
                     <Box sx={{ padding: "8px 4px 8px 4px" }}>
                        <TabPanel value={tabValue} index={0}>
                           <FormField {...endpointUrl} value={values.endpointUrl} disabled multiline secondaryCopySuffix={`&targetUserId=${targetUserId}&displayExecutionLogs=true`} />
                           <FormField {...triggerName} value={values.triggerName} disabled={isFlowActive || !allowEditWorkflow} error={touched.triggerName && errors.triggerName} />
                           <FormField
                              {...description}
                              name={description.name}
                              placeholder={description.placeholder}
                              error={touched.description && errors.description}
                              type={description.type}
                              value={values.description}
                              disabled={isFlowActive || !allowEditWorkflow}
                              multiline={description.multiline}
                              rows={description.rows}
                           />
                           <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 1 }}>
                              <SoftTypography variant="h6" fontWeight="normal">
                                 Input Parameters
                              </SoftTypography>
                              {!isFlowActive && allowEditWorkflow && (
                                 <Tooltip title="Add parameter">
                                    <IconButton size="small" sx={{ ml: 1 }} onClick={() => setFieldValue("parameters", [...values.parameters, { paramName: "", paramType: "", paramDescription: "" }])}>
                                       <ThemedAddCircleOutlineIcon fontSize="medium" />
                                    </IconButton>
                                 </Tooltip>
                              )}
                           </Box>
                           {values.parameters &&
                              values.parameters.map((param, idx) => {
                                 const paramTypeValue =
                                    typeof param.paramType === "object" && param.paramType !== null ? param.paramType : parameters.itemField.paramType.options.find((opt) => opt.value === param.paramType) || null;

                                 return (
                                    <Box key={idx} sx={{ mt: 2 }}>
                                       <Box
                                          sx={{
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "flex-start"
                                          }}
                                       >
                                          <SoftTypography variant="h6" fontWeight="normal">
                                             Parameter {idx + 1}
                                          </SoftTypography>
                                          {!isFlowActive && allowEditWorkflow && (
                                             <Tooltip title="Remove parameter">
                                                <IconButton
                                                   onClick={() =>
                                                      setFieldValue(
                                                         "parameters",
                                                         values.parameters.filter((_, i) => i !== idx)
                                                      )
                                                   }
                                                   size="small"
                                                >
                                                   <ThemedTrashIcon width="19px" height="19px" />
                                                </IconButton>
                                             </Tooltip>
                                          )}
                                       </Box>
                                       <FormField
                                          {...parameters.itemField.paramName}
                                          name={`parameters[${idx}].paramName`}
                                          value={param.paramName}
                                          disabled={isFlowActive || !allowEditWorkflow}
                                          error={touched.parameters && touched.parameters[idx] && touched.parameters[idx].paramName && errors.parameters && errors.parameters[idx] && errors.parameters[idx].paramName}
                                       />
                                       <FormField
                                          {...parameters.itemField.paramType}
                                          name={`parameters[${idx}].paramType`}
                                          value={paramTypeValue}
                                          options={parameters.itemField.paramType.options}
                                          disabled={isFlowActive || !allowEditWorkflow}
                                          error={touched.parameters?.[idx]?.paramType && errors.parameters?.[idx]?.paramType}
                                       />
                                       <FormField {...parameters.itemField.paramDescription} name={`parameters[${idx}].paramDescription`} value={param.paramDescription} disabled={isFlowActive || !allowEditWorkflow} />
                                    </Box>
                                 );
                              })}
                        </TabPanel>
                        <TabPanel value={tabValue} index={1}>
                           <FormField {...notes} value={values.notes} disabled={isFlowActive || !allowEditWorkflow} />
                        </TabPanel>
                     </Box>
                  </Form>
               )}
            </Formik>
            <DiscardChangesDialog onAccept={closeForm} />
         </TriggerFormWrapper>
         <TabPanel value={tabValue} index={2}>
            <Comments node_id={selectedTrigger?.type} closeForm={closeForm} />
         </TabPanel>
      </>
   );
};

export default memo(McpTriggerForm);
